import { useCallback, useEffect, useState } from 'react';
import { hrApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { TablePagination } from '@/components/ui/TablePagination';
import { usePagination } from '@/hooks/usePagination';
import type { HrPayrollRun } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { downloadBlob } from '@/utils/downloadBlob';
import { notify } from '@/core/toast/notify';

const PORTALS = [
  { code: 'EPFO' as const, name: 'EPFO (India)', url: 'https://www.epfindia.gov.in/site_en/index.php', desc: 'Provident fund ECR filing' },
  { code: 'ESIC' as const, name: 'ESIC (India)', url: 'https://www.esic.in/', desc: 'Employee state insurance' },
  { code: 'TRACES' as const, name: 'TRACES (TDS)', url: 'https://www.tdscpc.gov.in/app/login.xhtml', desc: 'Tax deducted at source' },
];

export function HrStatutoryPage() {
  const pagination = usePagination();
  const [runs, setRuns] = useState<HrPayrollRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [filings, setFilings] = useState<Record<number, Array<{ portalCode: string; statusCode: string; referenceNumber?: string }>>>({});

  const load = useCallback(() => {
    setLoading(true);
    hrApi
      .listPayrollRuns({ page: pagination.page, size: pagination.size })
      .then((r) => {
        setRuns(r.content);
        pagination.applyPageResponse(r);
      })
      .catch((e) => showApiError(e, 'Failed to load payroll runs'))
      .finally(() => setLoading(false));
  }, [pagination.page, pagination.size, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const loadFilings = async (runId: number) => {
    try {
      const list = await hrApi.listStatutoryFilings(runId);
      setFilings((prev) => ({ ...prev, [runId]: list }));
    } catch {
      /* optional */
    }
  };

  const downloadStatutory = async (runId: number) => {
    try {
      const blob = await hrApi.downloadStatutoryIndiaCsv(runId);
      downloadBlob(blob, `statutory-india-${runId}.csv`);
      notify.success('Statutory CSV downloaded');
    } catch (e) {
      showApiError(e, 'Download failed');
    }
  };

  const submitFiling = async (runId: number, portalCode: 'EPFO' | 'ESIC' | 'TRACES') => {
    try {
      const result = await hrApi.submitStatutoryFiling(runId, portalCode);
      notify.success(`${portalCode} filing ${result.statusCode} — ref ${result.referenceNumber ?? result.id}`);
      await loadFilings(runId);
    } catch (e) {
      showApiError(e, `${portalCode} filing failed`);
    }
  };

  return (
    <div>
      <PageHeader
        title="Statutory filing"
        description="Automated portal submission (EPFO ECR, ESIC, TRACES) plus manual portal links"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'HR', to: '/hr' }, { label: 'Statutory' }]}
      />
      <div className="grid gap-4 lg:grid-cols-3 mb-6">
        {PORTALS.map((p) => (
          <a
            key={p.code}
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            className="card p-4 hover:border-primary-400 transition"
          >
            <h3 className="font-semibold text-primary-700">{p.name}</h3>
            <p className="text-sm text-slate-600 mt-1">{p.desc}</p>
          </a>
        ))}
      </div>
      <div className="card p-4">
        <h3 className="font-semibold mb-3">Payroll runs — export &amp; auto-file</h3>
        <ul className="text-sm space-y-4">
          {runs.map((r) => (
            <li key={r.id} className="border-b pb-3">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <span>
                  {r.runCode} — {r.statusCode}
                </span>
                <Button variant="secondary" onClick={() => void downloadStatutory(r.id)}>
                  Download CSV
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {PORTALS.map((p) => (
                  <Button key={p.code} variant="secondary" onClick={() => void submitFiling(r.id, p.code)}>
                    File {p.code}
                  </Button>
                ))}
                <Button variant="secondary" onClick={() => void loadFilings(r.id)}>
                  Refresh status
                </Button>
              </div>
              {(filings[r.id] ?? []).length > 0 && (
                <ul className="mt-2 text-xs text-slate-600">
                  {filings[r.id].map((f, i) => (
                    <li key={i}>
                      {f.portalCode}: {f.statusCode} {f.referenceNumber ? `(${f.referenceNumber})` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
          {runs.length === 0 && !loading && <li className="text-slate-500">No payroll runs yet</li>}
        </ul>
        <TablePagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          onPageChange={pagination.setPage}
          loading={loading}
          hasRows={runs.length > 0}
        />
      </div>
    </div>
  );
}
